import { useEffect, useState } from "react";
import {
  DEFAULT_CENTRE,
  parseLatLng,
  placeCentre,
  setBearing,
  resizeFrame,
} from "../utils/geoAlign";

export const STEP = { FIND: 0, ALIGN: 1 };

const round = (v, digits) => Math.round(v * 10 ** digits) / 10 ** digits;

/**
 * What 套用 hands back to the form: the 0,0 corner, the bearing and the size.
 * The size goes to the centimetre the backend stores; if that moves it, the
 * corner is recomputed so the centre stays where it was drawn.
 */
export const appliedFrame = (ref, floor) => {
  const size = { width: round(floor.width, 2), height: round(floor.height, 2) };
  const same = size.width === floor.width && size.height === floor.height;
  const corner = same ? ref : resizeFrame(ref, floor, size).ref;
  return {
    lat: round(corner.lat, 7),
    lng: round(corner.lng, 7),
    rotationDeg: round(corner.rotationDeg, 3),
    ...size,
  };
};

/**
 * The 對齊地圖 state. `ref` is always the stored form (0,0 corner + bearing)
 * and `floor` the size being drawn; pastes, clicks and the centre readouts
 * speak about the site CENTRE and are converted here. Each open starts from
 * the form's values; the paste text is kept between opens. Placing the site
 * in ① moves on to ②.
 */
export const useGeoAlign = ({ open, geo, dimensions }) => {
  const [ref, setRef] = useState(null);
  const [floor, setFloor] = useState(dimensions);
  const [step, setStep] = useState(STEP.FIND);
  const [focus, setFocus] = useState(null);
  const [paste, setPaste] = useState("");
  const [pasteError, setPasteError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const start =
      geo.lat !== null && geo.lng !== null
        ? { lat: geo.lat, lng: geo.lng, rotationDeg: geo.rotationDeg }
        : null;
    const pasted = parseLatLng(paste);
    setRef(start);
    setFloor({ width: dimensions.width, height: dimensions.height });
    setStep(start ? STEP.ALIGN : STEP.FIND);
    setPasteError(null);
    setFocus(start ? { ref: start } : pasted.error ? DEFAULT_CENTRE : pasted);
  }, [open]);

  const place = (next, soft = false) => {
    setRef(next);
    setFocus({ ref: next, soft });
    setStep(STEP.ALIGN);
  };
  const bearing = ref ? ref.rotationDeg : 0;

  const jump = () => {
    const parsed = parseLatLng(paste);
    setPasteError(parsed.error || null);
    if (parsed.error) return;
    place(placeCentre(bearing, [parsed.lat, parsed.lng], floor));
  };

  const clickMap = (latLng) => {
    if (step === STEP.FIND || !ref) {
      place(placeCentre(bearing, latLng, floor), true);
    }
  };

  /** A resize from the map: the new corner and the new size together. */
  const setFrame = (frame) => {
    setRef(frame.ref);
    setFloor(frame.floor);
  };

  const setCentre = (lat, lng) =>
    place(placeCentre(bearing, [lat, lng], floor), true);
  const setAngle = (deg) => setRef(setBearing(ref, floor, deg));

  return {
    ref,
    setRef,
    floor,
    setFrame,
    step,
    setStep,
    focus,
    paste,
    setPaste,
    pasteError,
    jump,
    clickMap,
    setCentre,
    setAngle,
  };
};
